import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, X, Loader2, Clock, RefreshCw, Mail, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { value: 'pending_admin_review', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const StatusPill = ({ status }) => {
  const map = {
    pending_otp: { text: 'Awaiting OTP', cls: 'bg-amber-100 text-amber-700' },
    pending_admin_review: { text: 'Pending review', cls: 'bg-blue-100 text-blue-700' },
    approved: { text: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
    rejected: { text: 'Rejected', cls: 'bg-red-100 text-red-700' },
    expired: { text: 'Expired', cls: 'bg-gray-100 text-gray-600' },
    cancelled: { text: 'Cancelled', cls: 'bg-gray-100 text-gray-600' },
  };
  const s = map[status] || { text: status, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.text}</span>;
};

const ContactChangeRequestsModal = ({ open, onClose, adminToken }) => {
  const [filter, setFilter] = useState('pending_admin_review');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [reason, setReason] = useState({});

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contact-change-requests?status=${filter}`, { headers });
      setRequests(res.data?.requests || []);
    } catch (e) {
      console.error('Error fetching contact change requests:', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchRequests(); /* eslint-disable-next-line */ }, [open, filter]);

  if (!open) return null;

  const decide = async (req, decision) => {
    if (decision === 'reject' && !reason[req.id]) {
      alert('Please add a short reason before rejecting.');
      return;
    }
    setActingId(req.id);
    try {
      await axios.post(
        `${API}/admin/contact-change-requests/${req.id}/${decision}`,
        { admin_note: reason[req.id] || null },
        { headers },
      );
      await fetchRequests();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Action failed');
    } finally { setActingId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="admin-contact-change-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Contact Change Requests</h2>
            <p className="text-xs text-gray-500">Approve or reject email / phone changes from providers and doctors.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchRequests} className="p-2 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100" title="Refresh">
              <RefreshCw size={16} />
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100" data-testid="admin-contact-change-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="px-6 py-3 border-b flex flex-wrap gap-2">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 text-xs rounded-full border ${filter === f.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
              data-testid={`admin-contact-change-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500"><Loader2 className="animate-spin mr-2" />Loading…</div>
          ) : requests.length === 0 ? (
            <div className="text-center py-16 text-gray-500">No requests in this view.</div>
          ) : (
            <div className="space-y-3">
              {requests.map(req => (
                <div key={req.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-sm transition" data-testid={`admin-contact-change-row-${req.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {req.change_type === 'email' ? <Mail size={14} className="text-blue-600" /> : <Phone size={14} className="text-emerald-600" />}
                        <span className="font-semibold text-gray-900">
                          {req.user_name || req.user_email || req.user_id}
                        </span>
                        <span className="text-xs text-gray-500">({req.user_user_type})</span>
                        <StatusPill status={req.status} />
                      </div>
                      <div className="text-sm text-gray-700 break-all">
                        <span className="text-gray-500">From:</span>{' '}
                        <code className="bg-gray-50 px-1 py-0.5 rounded">{req.current_value || '—'}</code>{' '}
                        <span className="text-gray-500">→ To:</span>{' '}
                        <code className="bg-gray-50 px-1 py-0.5 rounded">{req.requested_value}</code>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1"><Clock size={12} />{new Date(req.created_at).toLocaleString()}</span>
                        {req.otp_verified_at && (
                          <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} />OTP verified</span>
                        )}
                      </div>
                      {req.admin_note && (
                        <div className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-2">
                          <b>Admin note:</b> {req.admin_note}
                        </div>
                      )}
                    </div>

                    {req.status === 'pending_admin_review' && (
                      <div className="flex flex-col gap-2 w-full md:w-72">
                        <input
                          type="text"
                          placeholder="Optional note / reason if rejecting"
                          value={reason[req.id] || ''}
                          onChange={(e) => setReason(prev => ({ ...prev, [req.id]: e.target.value }))}
                          className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                          data-testid={`admin-contact-change-note-${req.id}`}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => decide(req, 'approve')}
                            disabled={actingId === req.id}
                            className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            data-testid={`admin-contact-change-approve-${req.id}`}
                          >
                            {actingId === req.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                          </button>
                          <button
                            onClick={() => decide(req, 'reject')}
                            disabled={actingId === req.id}
                            className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            data-testid={`admin-contact-change-reject-${req.id}`}
                          >
                            <XCircle size={12} /> Reject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactChangeRequestsModal;
